import Highcharts from 'highcharts'
import { HighchartsReact } from 'highcharts-react-official'
import { useCallback, useMemo, useEffect } from 'react'

import type { Options, SeriesColumnOptions } from 'highcharts'
import type { ReactElement } from 'react'

import BlockTitle from '~/components/Blocks/Title'
import GridItemLayout from '~/components/GridItemLayout'
import { analyticsColors } from '~/const/chart'
import dayjsUtil from '~/utils/dayjs'
import { getQuarter } from '~/utils/home'
import { formatValue } from '~/utils/number'

import './styles.scss'

interface Props {
  graphData?: {
    data: number[][]
    name: string
  }[]
  target?: number
  title: string
}

interface PlotBand {
  color: string
  from: number
  to: number
}

const GraphBlock = ({ graphData, target, title }: Props): ReactElement => {
  const getPlotBands = useCallback((): PlotBand[] => {
    let hasZoneStarted = false
    let startZoneX = -0.5
    let from = 0
    let to = 0

    const plotBands = []

    if (graphData && target) {
      for (const elem of graphData[0].data) {
        if (elem[1] > target) {
          // start a zone or into a zone already
          if (!hasZoneStarted) {
            hasZoneStarted = true
            from = startZoneX
          }
          to = startZoneX + 1
        } else {
          // if zone started, end zone
          if (hasZoneStarted) {
            plotBands.push({ color: '#ffcdb6', from, to })
            hasZoneStarted = false
            from = 0
            to = 0
          }
        }
        startZoneX++
      }

      if (hasZoneStarted) {
        plotBands.push({ color: '#ffcdb6', from, to })
      }
    }

    return plotBands
  }, [graphData, target])

  const formattedData: SeriesColumnOptions[] = useMemo(
    () => [
      {
        data: graphData?.[0].data.map((el) => [dayjsUtil(el[0]).format('DD MMM YYYY'), el[1]]) || [],
        name: 'Monthly Active Users',
        type: 'column',
      },
    ],
    [graphData]
  )

  const options: Options = useMemo(
    () => ({
      chart: {
        animation: false,
        events: {
          load() {
            setTimeout(() => {
              this.reflow.bind(this)
            }, 1000)
          },
        },
      },
      colors: ['#ff5301'],
      credits: {
        enabled: false,
      },
      exporting: {
        enabled: false,
      },
      legend: {
        align: 'center',
        alignColumns: false,
        enabled: true,
        itemMarginTop: 1,
        layout: 'horizontal',
        maxHeight: 75,
        squareSymbol: true,
        symbolHeight: 12,
        symbolRadius: 0,
        symbolWidth: 15,
        verticalAlign: 'top',
      },
      navigator: {
        enabled: false,
      },
      rangeSelector: {
        enabled: false,
      },
      series: formattedData,
      title: undefined,
      tooltip: {
        backgroundColor: '#FFFFFF',
        borderColor: '#1d1d1d',
        footerFormat: '</div>',
        headerFormat: '<div><div><b>{point.key}</b><div><br/>',
        padding: 12,
        pointFormatter: function () {
          if (this.y) {
            const formatedLabel = `<div><span style="color: ${this.color}">• </span><span style="color: ${
              this.color
            }">${this.series.name}:  </span> <b>${formatValue(this.y)}</b></div>`

            return formatedLabel
          }
          return ''
        },
        shared: true,
        split: false,
        useHTML: true,
        xDateFormat: '%b %d, %Y',
      },
      xAxis: {
        min: 0,
        plotBands: getPlotBands(),
        scrollbar: {
          enabled: false,
        },
        type: 'category',
      },
      yAxis: {
        max: target ? target + 1000000 : null,
        plotLines: [
          {
            color: analyticsColors[1],
            dashStyle: 'Solid',
            label: {
              align: 'left',
              style: {
                color: analyticsColors[1],
                fontFamily: 'ModernGothic',
                fontSize: '1.3rem',
                fontWeight: '600',
                lineHeight: '1.6rem',
              },
              text: `Q${getQuarter()} Objective`,
              x: 0,
              y: -7,
            },
            value: target,
            width: 2,
            zIndex: 5,
          },
        ],
        title: undefined,
      },
    }),
    [formattedData, getPlotBands, target]
  )

  useEffect(() => {
    // NOTE: chart does not resize on 13' if we don't trigger it manually :man_shrugging:
    const timeout = setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 0)

    return () => {
      clearTimeout(timeout)
    }
  }, [])

  return (
    <GridItemLayout className="graph-block-component">
      <BlockTitle className="graph-block-component__title">{title}</BlockTitle>
      <div className="graph-wrapper">
        <HighchartsReact
          containerProps={{
            style: {
              height: '99%',
            },
          }}
          highcharts={Highcharts}
          options={options}
        />
      </div>
    </GridItemLayout>
  )
}

export default GraphBlock
